import Joi from 'joi';
import { Countries } from '../enums/countries.enum';

const stringWithNumbersOnlyRegEx = /^\d+$/; // @ref https://github.com/sideway/joi/issues/1157#issuecomment-293538673

export const CreateAccountSchema = Joi.object({
  city: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'City is required.',
    'string.base': 'City is invalid.',
    'string.empty': 'City is invalid.',
    'string.min': 'City must have at most 100 characters only.',
    'string.max': 'City must have at most 100 characters only.',
  }),
  line1: Joi.string().trim().min(1).max(64).required().messages({
    'any.required': 'Street address is required.',
    'string.base': 'Street address is invalid.',
    'string.empty': 'Street address is invalid.',
    'string.min': 'Street address must have at most 64 characters only.',
    'string.max': 'Street address must have at most 64 characters only.',
  }),
  buildingNameAndNumber: Joi.string()
    .trim()
    .min(1)
    .max(200)
    .optional()
    .allow('')
    .messages({
      'string.base': 'Building name and number is invalid.',
      'string.empty': 'Building name and number is invalid.',
      'string.min':
        'Building name and number must have at most 200 characters only.',
      'string.max':
        'Building name and number must have at most 200 characters only.',
    }),
  state: Joi.string().trim().min(1).max(64).required().messages({
    'string.base': 'Province is invalid.',
    'string.empty': 'Province is invalid.',
    'string.min': 'Province must have at most 64 characters only.',
    'string.max': 'Province must have at most 64 characters only.',
  }),
  postalCode: Joi.string()
    .trim()
    .min(4)
    .max(10)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Postal code is required.',
      'string.base': 'Postal code is invalid.',
      'string.empty': 'Postal code is invalid.',
      'string.min': 'Postal code must have 4 to 10 characters only.',
      'string.max': 'Postal code must have 4 to 10 characters only.',
      'string.pattern.base': 'Postal code is invalid.',
    }),
  barangay: Joi.string().trim().min(1).max(200).optional().allow('').messages({
    'string.base': 'Barangay is invalid.',
    'string.empty': 'Barangay is invalid.',
    'string.min': 'Barangay must have at most 200 characters only.',
    'string.max': 'Barangay must have at most 200 characters only.',
  }),
  countryId: Joi.number()
    .valid(Countries.PHILIPPINES)
    .optional()
    .allow('')
    .messages({
      'any.only': 'Country is invalid.',
      'number.base': 'Country is invalid.',
    }),
  fax: Joi.string().trim().min(1).max(30).optional().allow('').messages({
    'any.required': 'Fax is required.',
    'string.empty': 'Fax is invalid.',
    'string.min': 'Fax must have at most 30 characters only.',
    'string.max': 'Fax must have at most 30 characters only.',
  }),
  firstName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'First name is required.',
    'string.base': 'First name is invalid.',
    'string.empty': 'First name is invalid.',
    'string.min': 'First name must have at most 100 characters only.',
    'string.max': 'First name must have at most 100 characters only.',
  }),
  middleName: Joi.string().trim().min(1).max(100).optional().messages({
    'string.base': 'Middle name is invalid.',
    'string.empty': 'Middle name is invalid.',
    'string.min': 'Middle name must have at most 100 characters only.',
    'string.max': 'Middle name must have at most 100 characters only.',
  }),
  lastName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Last name is required.',
    'string.base': 'Last name is invalid.',
    'string.empty': 'Last name is invalid.',
    'string.min': 'Last name must have at most 100 characters only.',
    'string.max': 'Last name must have at most 100 characters only.',
  }),
  username: Joi.string().trim().min(1).max(50).required().messages({
    'any.required': 'Username is required.',
    'string.base': 'Username is invalid.',
    'string.empty': 'Username is invalid.',
    'string.min': 'Username must have at most 50 characters only.',
    'string.max': 'Username must have at most 50 characters only.',
  }),
  // 22 characters for password results to 44 character ciphertext
  // Note: Database password max characters is 50
  password: Joi.string()
    .trim()
    .min(8)
    .max(22)
    .pattern(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{7,}$/)
    .required()
    .messages({
      'any.required': 'Password is required.',
      'string.base': 'Password is invalid.',
      'string.pattern.base':
        'Password must have at least 1 uppercase and lowercase character, a number, and special character.',
      'string.empty': 'Password is invalid.',
      'string.min': 'Password must have 8 to 22 characters only.',
      'string.max': 'Password must have 8 to 22 characters only.',
    }),
  pin: Joi.string()
    .trim()
    .length(4)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Pin is required.',
      'string.base': 'Pin is invalid.',
      'string.empty': 'Pin is invalid.',
      'string.pattern.base': 'Pin is invalid.',
      'string.length': 'Pin must have 4 characters only.',
    }),
  primary: Joi.string()
    .trim()
    .min(1)
    .max(30)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Primary contact number is required.',
      'string.base': 'Primary contact number is invalid.',
      'string.empty': 'Primary contact number is invalid.',
      'string.pattern.base': 'Primary contact number is invalid.',
      'string.min':
        'Primary contact number must have at most 30 characters only.',
      'string.max':
        'Primary contact number must have at most 30 characters only.',
    }),
  mobile: Joi.string()
    .trim()
    .min(1)
    .max(30)
    .regex(stringWithNumbersOnlyRegEx)
    .optional()
    .allow('')
    .messages({
      'string.base': 'Mobile phone number is invalid.',
      'string.empty': 'Mobile phone number is invalid.',
      'string.pattern.base': 'Mobile phone number is invalid.',
      'string.min': 'Mobile phone number must have at most 30 characters only.',
      'string.max': 'Mobile phone number must have at most 30 characters only.',
    }),
  email: Joi.string().trim().max(100).required().messages({
    'any.required': 'Email is required.',
    'string.base': 'Email is invalid.',
    'string.empty': 'Email is invalid.',
    'string.min': 'Email must have at most 100 characters only.',
    'string.max': 'Email must have at most 100 characters only.',
  }),
  noMiddleName: Joi.any(),
  confirmPassword: Joi.any().valid(Joi.ref('password')).messages({
    'any.only': 'Passwords do not match.',
  }),
});

export const BranchCreateSchema = Joi.object({
  firstName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'First name is required.',
    'string.base': 'First name is invalid.',
    'string.empty': 'First name is invalid.',
    'string.min': 'First name must have at most 100 characters only.',
    'string.max': 'First name must have at most 100 characters only.',
  }),
  middleName: Joi.string().trim().min(1).max(100).optional().messages({
    'string.base': 'Middle name is invalid.',
    'string.empty': 'Middle name is invalid.',
    'string.min': 'Middle name must have at most 100 characters only.',
    'string.max': 'Middle name must have at most 100 characters only.',
  }),
  lastName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Last name is required.',
    'string.base': 'Last name is invalid.',
    'string.empty': 'Last name is invalid.',
    'string.min': 'Last name must have at most 100 characters only.',
    'string.max': 'Last name must have at most 100 characters only.',
  }),
  username: Joi.string().trim().min(1).max(50).required().messages({
    'any.required': 'Username is required.',
    'string.base': 'Username is invalid.',
    'string.empty': 'Username is invalid.',
    'string.min': 'Username must have at most 50 characters only.',
    'string.max': 'Username must have at most 50 characters only.',
  }),
  // 22 characters for password results to 44 character ciphertext
  // Note: Database password max characters is 50
  password: Joi.string()
    .trim()
    .min(8)
    .max(22)
    .pattern(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{7,}$/)
    .required()
    .messages({
      'any.required': 'Password is required.',
      'string.base': 'Password is invalid.',
      'string.pattern.base':
        'Password must have at least 1 uppercase and lowercase character, a number, and special character.',
      'string.empty': 'Password is invalid.',
      'string.min': 'Password must have 8 to 22 characters only.',
      'string.max': 'Password must have 8 to 22 characters only.',
    }),
  pin: Joi.string()
    .trim()
    .length(4)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Pin is required.',
      'string.base': 'Pin is invalid.',
      'string.empty': 'Pin is invalid.',
      'string.pattern.base': 'Pin is invalid.',
      'string.length': 'Pin must have 4 characters only.',
    }),
  primary: Joi.string()
    .trim()
    .min(1)
    .max(30)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Primary contact number is required.',
      'string.base': 'Primary contact number is invalid.',
      'string.empty': 'Primary contact number is invalid.',
      'string.pattern.base': 'Primary contact number is invalid.',
      'string.min':
        'Primary contact number must have at most 30 characters only.',
      'string.max':
        'Primary contact number must have at most 30 characters only.',
    }),

  email: Joi.string().trim().max(100).required().messages({
    'any.required': 'Email is required.',
    'string.base': 'Email is invalid.',
    'string.empty': 'Email is invalid.',
    'string.min': 'Email must have at most 100 characters only.',
    'string.max': 'Email must have at most 100 characters only.',
  }),
  noMiddleName: Joi.any(),
  confirmPassword: Joi.any().valid(Joi.ref('password')).messages({
    'any.only': 'Passwords do not match.',
  }),
});
